import { useEffect, useLayoutEffect, useRef, useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Box, Button, CircularProgress, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from "@mui/material";

import { formatMoney } from "../../../../../shared/utils/formatMoney";
import {
  getSalesImportTreasuryAccounts,
  getSalesImportTreasuryCategories,
  getSalesImportTreasuryPeriodBalances,
  getSalesImportTreasuryPeriods,
  saveSalesImportTreasury,
} from "../api/salesImportsApi";
import { getSalesImportErrorMessage } from "../utils/salesImportError";
import {
  availableTreasuryAccounts,
  compatibleTreasuryPeriods,
  formatTreasuryAmountFromCanonical,
  formatTreasuryAmountInput,
  incomeTreasuryCategories,
  parseTreasuryAmountCents,
  preferredSalesTreasuryCategoryId,
  treasuryAmountFromCents,
  treasuryDistribution,
} from "../utils/salesImportTreasuryModel";

const periodFormatter = new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" });
const allocationGridColumns = "minmax(150px, 1fr) minmax(130px, 1fr) 60px";

function dateLabel(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return "—";
  return periodFormatter.format(new Date(`${value}T12:00:00Z`));
}

function isAbort(error) {
  return error?.name === "AbortError";
}

export default function SalesImportStepThree({ draft, config, form, notice = "", onFormChange, onConfigured, onSavingChange }) {
  const [periods, setPeriods] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [periodId, setPeriodId] = useState(form?.periodId || config?.period?.id || "");
  const [categoryId, setCategoryId] = useState(form?.categoryId || config?.category?.id || "");
  const [allocations, setAllocations] = useState(() => form?.allocations?.length
    ? form.allocations
    : config?.allocations?.length
    ? config.allocations.map((allocation) => ({ accountId: allocation.accountId, amount: formatTreasuryAmountFromCanonical(allocation.amount) }))
    : [{ accountId: "", amount: formatTreasuryAmountFromCanonical(draft.totalSalesAmount) }]);
  const [loading, setLoading] = useState(true);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [serverErrors, setServerErrors] = useState([]);
  const [retry, setRetry] = useState(0);
  const requestId = useRef(0);
  const amountInputs = useRef([]);
  const pendingCursor = useRef(null);
  const configuredRef = useRef(onConfigured);
  configuredRef.current = onConfigured;
  const distribution = treasuryDistribution(allocations, draft.totalSalesAmount);
  const selectedPeriod = periods.find((period) => period.id === Number(periodId));
  const selectedCategory = categories.find((category) => category.id === Number(categoryId));
  const hasIncompleteAllocation = allocations.some((allocation) => {
    const amount = parseTreasuryAmountCents(allocation.amount);
    return !accounts.some((account) => account.id === Number(allocation.accountId)) || amount === null || amount <= 0n;
  });

  useEffect(() => {
    onSavingChange(loading || accountsLoading || saving);
    return () => onSavingChange(false);
  }, [loading, accountsLoading, saving, onSavingChange]);

  useEffect(() => {
    configuredRef.current(null);
  }, []);

  useEffect(() => {
    onFormChange?.({ periodId, categoryId, allocations });
  }, [periodId, categoryId, allocations, onFormChange]);

  useLayoutEffect(() => {
    const pending = pendingCursor.current;
    if (!pending) return;
    pendingCursor.current = null;
    const input = amountInputs.current[pending.index];
    if (input && document.activeElement === input) input.setSelectionRange(pending.position, pending.position);
  }, [allocations]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    Promise.all([
      getSalesImportTreasuryPeriods({ signal: controller.signal }),
      getSalesImportTreasuryCategories({ signal: controller.signal }),
      getSalesImportTreasuryAccounts({ signal: controller.signal }),
    ]).then(([periodResponse, categoryResponse, accountResponse]) => {
      if (controller.signal.aborted) return;
      const compatible = compatibleTreasuryPeriods(periodResponse, draft.salesDateFrom, draft.salesDateTo);
      const incomeCategories = incomeTreasuryCategories(categoryResponse);
      setPeriods(compatible);
      setCategories(incomeCategories);
      setAllAccounts(accountResponse);
      setPeriodId((current) => current || (compatible.length === 1 ? compatible[0].id : ""));
      setCategoryId((current) => current || preferredSalesTreasuryCategoryId(incomeCategories) || "");
    }).catch((requestError) => {
      if (!controller.signal.aborted && !isAbort(requestError)) {
        setError(getSalesImportErrorMessage(requestError, "No se pudieron cargar las opciones de Tesorería."));
      }
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [draft.salesDateFrom, draft.salesDateTo, retry]);

  useEffect(() => {
    if (!periodId || loading || error) {
      setAccounts([]);
      return undefined;
    }
    const controller = new AbortController();
    setAccountsLoading(true);
    getSalesImportTreasuryPeriodBalances(periodId, { signal: controller.signal })
      .then((response) => {
        if (!controller.signal.aborted) setAccounts(availableTreasuryAccounts(allAccounts, response));
      })
      .catch((requestError) => {
        if (!controller.signal.aborted && !isAbort(requestError)) {
          setAccounts([]);
          setError(getSalesImportErrorMessage(requestError, "No se pudieron cargar las cuentas del período."));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setAccountsLoading(false);
      });
    return () => controller.abort();
  }, [periodId, allAccounts, loading, error]);

  useEffect(() => {
    if (!config || loading || accountsLoading || error) return;
    if (!periods.some((period) => period.id === Number(config.period?.id)) ||
      !categories.some((category) => category.id === Number(config.category?.id)) ||
      config.allocations?.some((allocation) => !accounts.some((account) => account.id === Number(allocation.accountId)))) {
      configuredRef.current(null);
    }
  }, [config, loading, accountsLoading, error, periods, categories, accounts]);

  useEffect(() => {
    if (loading || accountsLoading || error || !selectedPeriod || !selectedCategory || !distribution.exact ||
      allocations.some((allocation) => !accounts.some((account) => account.id === Number(allocation.accountId)))) {
      return undefined;
    }
    const controller = new AbortController();
    const currentId = ++requestId.current;
    const timer = setTimeout(async () => {
      setSaving(true);
      setServerErrors([]);
      try {
        const response = await saveSalesImportTreasury(draft.id, {
          expectedVersion: draft.version,
          periodId: Number(periodId),
          categoryId: Number(categoryId),
          allocations: allocations.map((allocation) => ({
            accountId: Number(allocation.accountId),
            amount: treasuryAmountFromCents(parseTreasuryAmountCents(allocation.amount)),
          })),
        }, { signal: controller.signal });
        if (controller.signal.aborted || currentId !== requestId.current) return;
        setServerErrors(Array.isArray(response.errors) ? response.errors : []);
        configuredRef.current(response.valid === true && response.persisted === true ? response : null);
      } catch (requestError) {
        if (!controller.signal.aborted && currentId === requestId.current && !isAbort(requestError)) {
          configuredRef.current(null);
          setError(getSalesImportErrorMessage(requestError, "No se pudo validar la distribución de Tesorería."));
        }
      } finally {
        if (!controller.signal.aborted && currentId === requestId.current) setSaving(false);
      }
    }, 350);
    return () => {
      clearTimeout(timer);
      controller.abort();
      requestId.current += 1;
      setSaving(false);
    };
  }, [accounts, accountsLoading, allocations, categoryId, draft.id, draft.version, distribution.exact, error, loading, periodId, selectedCategory, selectedPeriod]);

  function editPeriod(value) {
    setPeriodId(value);
    setAccounts([]);
    setServerErrors([]);
    setError("");
    configuredRef.current(null);
  }

  function editCategory(value) {
    setCategoryId(value);
    setServerErrors([]);
    setError("");
    configuredRef.current(null);
  }

  function editAllocation(index, patch) {
    setAllocations((current) => current.map((allocation, row) => row === index ? { ...allocation, ...patch } : allocation));
    setServerErrors([]);
    setError("");
    configuredRef.current(null);
  }

  function editAmount(index, event) {
    const input = event.target;
    const beforeCursor = input.value.slice(0, input.selectionStart ?? input.value.length);
    const logicalPosition = (beforeCursor.match(/[\d,]/g) || []).length;
    const amount = formatTreasuryAmountInput(input.value);
    let position = 0;
    let seen = 0;
    while (position < amount.length && seen < logicalPosition) {
      if (/[\d,]/.test(amount[position])) seen += 1;
      position += 1;
    }
    pendingCursor.current = { index, position };
    editAllocation(index, { amount });
  }

  function addAllocation() {
    const pending = parseTreasuryAmountCents(distribution.pending);
    setAllocations((current) => [...current, { accountId: "", amount: pending > 0n ? formatTreasuryAmountFromCanonical(treasuryAmountFromCents(pending)) : "" }]);
    setServerErrors([]);
    configuredRef.current(null);
  }

  function removeAllocation(index) {
    setAllocations((current) => current.filter((_, row) => row !== index));
    setServerErrors([]);
    configuredRef.current(null);
  }

  return (
    <Stack spacing={1.5} sx={{ height: "100%", minHeight: 0, overflowY: "auto", pr: 0.5 }}>
      <Typography component="h2" sx={{ color: "primary.900", fontSize: 18, fontWeight: 800 }}>Paso 3: Tesorería</Typography>
      {notice ? <Typography role="status" sx={{ p: 1.25, borderRadius: "14px", bgcolor: "warning.light", color: "primary.900", fontSize: 13 }}>{notice}</Typography> : null}
      {loading ? <Stack direction="row" spacing={1} alignItems="center" role="status"><CircularProgress size={18} /> <Typography>Cargando Tesorería…</Typography></Stack> : null}
      {error ? <Stack direction="row" spacing={1} alignItems="center"><Typography role="alert" color="error">{error}</Typography><Button onClick={() => { setError(""); setRetry((value) => value + 1); }}>Reintentar</Button></Stack> : null}
      {!loading && !error ? <>
        <Stack spacing={1.5} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: "22px", border: "1px solid", borderColor: "neutral.600", bgcolor: "secondary.200" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "160px minmax(0, 310px) auto" }, alignItems: "center", columnGap: 2, rowGap: 0.75 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 650, color: "primary.900" }}>Período de Tesorería:</Typography>
            <TextField select size="small" value={periodId} onChange={(event) => editPeriod(event.target.value)} inputProps={{ "aria-label": "Período de Tesorería" }} SelectProps={{ displayEmpty: true, renderValue: (value) => value ? `${dateLabel(selectedPeriod?.dateFrom)} – ${dateLabel(selectedPeriod?.dateTo)}` : "Seleccionar período" }} sx={{ width: "100%", "& .MuiOutlinedInput-root": { bgcolor: "neutral.50", borderRadius: "20px", overflow: "hidden" } }}>
              {periods.map((period) => <MenuItem key={period.id} value={period.id}>{dateLabel(period.dateFrom)} – {dateLabel(period.dateTo)}</MenuItem>)}
            </TextField>
            {selectedPeriod ? <Typography sx={{ justifySelf: { sm: "start" }, px: 1.5, py: 0.55, borderRadius: "999px", bgcolor: "success.main", color: "logoblanco", fontSize: 12, fontWeight: 750 }}>Estado abierto</Typography> : null}
          </Box>
          {!periods.length ? <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <Typography role="alert" sx={{ color: "error.main", fontSize: 12 }}>No existe un período abierto de Tesorería para estas ventas. Configurá el período correspondiente antes de continuar.</Typography>
            <Button type="button" onClick={() => setRetry((value) => value + 1)} sx={{ textTransform: "none", color: "primary.900", flexShrink: 0 }}>Actualizar períodos y cuentas</Button>
          </Stack> : null}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "160px minmax(0, 310px) auto" }, alignItems: "center", columnGap: 2, rowGap: 0.75 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 650, color: "primary.900" }}>Categoría:</Typography>
            <TextField select size="small" value={categoryId} onChange={(event) => editCategory(event.target.value)} inputProps={{ "aria-label": "Categoría de Tesorería" }} SelectProps={{ displayEmpty: true, renderValue: (value) => value ? selectedCategory?.name || "Seleccionar categoría" : "Seleccionar categoría" }} sx={{ width: "100%", "& .MuiOutlinedInput-root": { bgcolor: "neutral.50", borderRadius: "20px", overflow: "hidden" } }}>
              {categories.map((category) => <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>)}
            </TextField>
          </Box>
        </Stack>
        <Typography sx={{ fontSize: 13, color: "primary.900" }}>Distribución entre cuentas</Typography>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} sx={{ minHeight: 0, flex: 1 }}>
          <Box role="region" aria-label="Distribución entre cuentas" tabIndex={0} sx={{ flex: 1, minWidth: 0, border: "1px solid", borderColor: "neutral.600", borderRadius: "20px", overflow: "auto", overscrollBehavior: "contain" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: allocationGridColumns, gap: 1, minWidth: 430, bgcolor: "neutral.600", color: "logoblanco", p: 1.25, fontSize: 12, fontWeight: 700, position: "sticky", top: 0, zIndex: 1 }}><span>Cuenta</span><span>Importe</span><span>Acción</span></Box>
            {allocations.map((allocation, index) => {
              const selectedElsewhere = new Set(allocations.filter((_, row) => row !== index).map((row) => Number(row.accountId)));
              const accountError = serverErrors.find((issue) => issue.row === index && issue.field === "accountId")?.message ||
                (!allocation.accountId ? "Seleccioná una cuenta." :
                  !accountsLoading && !accounts.some((account) => account.id === Number(allocation.accountId)) ? "La cuenta no está disponible en este período." : "");
              const amountError = serverErrors.find((issue) => issue.row === index && issue.field === "amount")?.message ||
                (allocation.amount === "" ? "Ingresá un importe." :
                  parseTreasuryAmountCents(allocation.amount) === null || parseTreasuryAmountCents(allocation.amount) <= 0n ? "El importe debe ser positivo y tener hasta dos decimales." : "");
              return <Box key={index} sx={{ display: "grid", gridTemplateColumns: allocationGridColumns, gap: 1, minWidth: 430, p: 1.25, borderBottom: "1px solid", borderColor: "neutral.300", alignItems: "start" }}>
                <TextField select size="small" value={accounts.some((account) => account.id === Number(allocation.accountId)) ? allocation.accountId : ""} onChange={(event) => editAllocation(index, { accountId: event.target.value })} error={Boolean(accountError)} helperText={accountError} inputProps={{ "aria-label": `Cuenta destino ${index + 1}` }} SelectProps={{ displayEmpty: true, renderValue: (value) => value ? accounts.find((account) => account.id === Number(value))?.name || "Cuenta no disponible" : "Seleccionar cuenta" }} disabled={!periodId || accountsLoading}>
                  <MenuItem value="">Seleccionar cuenta</MenuItem>
                  {accounts.filter((account) => !selectedElsewhere.has(account.id)).map((account) => <MenuItem key={account.id} value={account.id}>{account.name}</MenuItem>)}
                </TextField>
                <TextField size="small" value={allocation.amount} onChange={(event) => editAmount(index, event)} inputRef={(input) => { amountInputs.current[index] = input; }} error={Boolean(amountError)} helperText={amountError} inputProps={{ inputMode: "decimal", "aria-label": `Importe de cuenta ${index + 1}` }} />
                <Tooltip title="Quitar cuenta" arrow>
                  <span style={{ alignSelf: "start", textAlign: "center" }}>
                    <IconButton
                      type="button"
                      disabled={allocations.length === 1}
                      aria-label={`Quitar cuenta ${index + 1}`}
                      onClick={() => removeAllocation(index)}
                      sx={{ color: "error.main", "&:hover": { bgcolor: "error.light" } }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>;
            })}
            <Button type="button" disabled={accountsLoading || hasIncompleteAllocation} onClick={addAllocation} sx={{ m: 1, width: "calc(100% - 16px)", border: "1px solid", borderColor: "neutral.600", textTransform: "none", color: "primary.900" }}>+ Agregar cuenta destino</Button>
          </Box>
          <Stack spacing={1} sx={{ alignSelf: "flex-start", minWidth: 225, p: 1.5, borderRadius: "20px", border: "1px solid", borderColor: "neutral.600", bgcolor: "secondary.200", fontSize: 12 }}>
            <Typography sx={{ fontSize: 12 }}>Fecha de movimiento: {dateLabel(draft.salesDateTo)}</Typography>
            <Typography sx={{ fontSize: 12 }}>Total ventas: {formatMoney(draft.totalSalesAmount)}</Typography>
            <Typography sx={{ fontSize: 12 }}>Total distribuido: {formatMoney(distribution.distributed)}</Typography>
            <Typography sx={{ fontSize: 12, color: distribution.excess !== "0.00" ? "error.main" : distribution.pending !== "0.00" ? "warning.dark" : "success.dark" }}>
              {distribution.excess !== "0.00" ? "Excedente" : "Pendiente"}: {formatMoney(distribution.excess !== "0.00" ? distribution.excess : distribution.pending)}
            </Typography>
          </Stack>
        </Stack>
        {serverErrors.filter((issue) => issue.row === null).map((issue) => <Typography key={issue.code} role="alert" sx={{ color: "error.main", fontSize: 12 }}>{issue.message}</Typography>)}
        {saving ? <Typography role="status" sx={{ fontSize: 12 }}>Validando distribución…</Typography> : null}
      </> : null}
    </Stack>
  );
}
